import { Order } from "../types";

interface DeliveryServiceWithTemplates {
    tracking_url_template?: string;
    label_url_template?: string;
}

/**
 * Replaces {consignment_id} in a URL template with the actual consignment ID.
 * Returns null if either argument is missing.
 */
function buildUrl(template: string | undefined, consignmentId: string | undefined): string | null {
    if (!template || !consignmentId) return null;
    return template.replace("{consignment_id}", encodeURIComponent(consignmentId));
}

/**
 * Returns the tracking URL for an order, or null if not available.
 * Requires both order.consignment_id and order.delivery_service.tracking_url_template.
 */
export function getTrackingUrl(order: Order): string | null {
    return buildUrl(order.delivery_service?.tracking_url_template, order.consignment_id);
}

/**
 * Returns the label printing URL for an order, or null if not available.
 * Requires both order.consignment_id and order.delivery_service.label_url_template.
 */
export function getLabelUrl(order: Order): string | null {
    return buildUrl(order.delivery_service?.label_url_template, order.consignment_id);
}

/**
 * Returns both URLs in one call. Useful for rendering action buttons.
 */
export function getCourierUrls(order: Order): { trackingUrl: string | null; labelUrl: string | null } {
    return {
        trackingUrl: getTrackingUrl(order),
        labelUrl: getLabelUrl(order),
    };
}

/**
 * Whether the order's consignment is a Steadfast parcel: it has a
 * consignment and its courier is Steadfast — or unset, since booking through
 * Steadfast saves the consignment without setting the order's courier.
 */
export function isSteadfastParcel(
    order: Pick<Order, "consignment_id"> & { delivery_service?: { name?: string } | null },
): boolean {
    if (!order.consignment_id?.toString().trim()) return false;
    const courier = order.delivery_service?.name;
    return !courier || /steadfast/i.test(courier);
}

/**
 * Builds URLs from a bare delivery service object + consignment ID.
 * Useful when you have the service but not a full Order object.
 */
export function buildCourierUrls(
    service: DeliveryServiceWithTemplates | undefined | null,
    consignmentId: string | undefined | null,
): { trackingUrl: string | null; labelUrl: string | null } {
    return {
        trackingUrl: buildUrl(service?.tracking_url_template, consignmentId ?? undefined),
        labelUrl: buildUrl(service?.label_url_template, consignmentId ?? undefined),
    };
}
